/**
 * BPG DCC (Dynamic Currency Conversion) — helpers for checkout card pay.
 *
 * check-dcc-availability (UNAUTHED):
 *   New card: { orderToken, cardToken } after cache-card-info (backend validates cardToken)
 *   Saved card: { orderToken, loginToken, payMethodShortNo } (aligned with query-install-plan saved)
 *
 * Probe timing matches query-install-plan: after we have a card identifier we always
 * call check-dcc-availability; the API returns dccAvailable (merchants without DCC get false).
 * UI (currency choice) only shows when parseDccOfferOrNull returns a full offer.
 */

/** Same gate as calling query-install-plan: need a non-empty card identifier to send. */
export function shouldAttemptDccCheck(cardIdentifier) {
  return !!cardIdentifier
}

/**
 * Card submit payloads usually include `siteConfig` but not top-level `payInfo`.
 * DCC overlay needs `payAmount` { amount, currency } for the merchant-currency row.
 */
export function resolveMerchantPayAmountForDcc(data, getState) {
  const fromTop = data && data.payInfo && data.payInfo.payAmount
  if (fromTop && fromTop.currency != null && fromTop.amount != null) return fromTop
  const fromNested = data && data.siteConfig && data.siteConfig.payInfo && data.siteConfig.payInfo.payAmount
  if (fromNested && fromNested.currency != null && fromNested.amount != null) return fromNested
  if (typeof getState === 'function') {
    const s = getState()
    const fromStore = s && s.siteConfig && s.siteConfig.payInfo && s.siteConfig.payInfo.payAmount
    if (fromStore && fromStore.currency != null && fromStore.amount != null) return fromStore
  }
  return undefined
}

/**
 * Normalized success payload for showing the DCC UI, or null to keep standard pay.
 */
function isDccAvailableTruthy(v) {
  if (v === true) return true
  if (typeof v === 'string' && v.trim().toLowerCase() === 'true') return true
  return false
}

export function parseDccOfferOrNull(body) {
  if (!body || body.request !== 'success') return null
  if (!isDccAvailableTruthy(body.dccAvailable) || !body.dccQuoteId) return null
  const dccAmt = body.dccTotalAmount
  if (!dccAmt || dccAmt.amount == null || dccAmt.amount === '') return null
  if (!dccAmt.currency) return null
  return body
}

export function getDccAmountDecimalPlaces(currency) {
  if (!currency) return 2
  const c = String(currency).toUpperCase()
  if (c === 'JPY') return 0
  if (c === 'KWD' || c === 'BHD') return 3
  return 2
}

/** Render markup as a human percent (API may send 2, 2.0, or 0.02). */
export function normalizeMarkupPercentDisplay(markupRate) {
  if (markupRate === null || markupRate === undefined || markupRate === '') return null
  const n = Number(markupRate)
  if (!Number.isFinite(n)) return String(markupRate)
  if (n > 0 && n <= 1 && !String(markupRate).includes('e')) {
    return (n * 100).toFixed(2)
  }
  return n.toFixed(2).replace(/\.?0+$/, '')
}

export function formatExchangeRateForDisplay(rate) {
  const n = Number(rate)
  if (!Number.isFinite(n)) return String(rate)
  const s = n.toFixed(8).replace(/\.?0+$/, '')
  return s || '0'
}

/** True when pay query-result `payResultInfo` includes DCC fields (4.6 API). */
export function payResultHasDccDetails(payResultInfo) {
  if (!payResultInfo) return false
  const ta = payResultInfo.dccTotalAmount
  return !!(
    payResultInfo.dccQuoteId ||
    (ta && ((ta.amount != null && ta.amount !== '') || ta.currency)) ||
    payResultInfo.dccExchangeRate != null ||
    payResultInfo.dccMarkupRate != null
  )
}

/** Cashierii `dccInfo.dccChoice` — see 4.1 / 4.3 / 4.4 / 4.5 (card-token-pay, confirm-pay, iframe variants). */
export const DCC_INFO_CHOICE_ACCEPT = 'ACCEPT'
export const DCC_INFO_CHOICE_DECLINE = 'DECLINE'

/**
 * Set `body.dccInfo` for card-token-pay / confirm-pay (+ iframe) per cashierii DccInfo:
 * `dccChoice` (ACCEPT|DECLINE), `dccQuoteId`, `dccTotalAmount` { amount, currency }; optional `exchangeRate`, `markupRate`.
 * Mutates `body` and returns it. No-op if quote or full DCC amount is missing.
 */
export function applyDccSelectionToPayBody(body, pending, dccChoice) {
  if (!body || !pending) return body
  if (dccChoice !== DCC_INFO_CHOICE_ACCEPT && dccChoice !== DCC_INFO_CHOICE_DECLINE) return body
  if (!pending.dccQuoteId) return body

  const dccAmt = pending.dccTotalAmount
  if (
    !dccAmt ||
    dccAmt.amount == null ||
    dccAmt.amount === '' ||
    !dccAmt.currency
  ) {
    return body
  }

  const dccInfo = {
    dccChoice,
    dccQuoteId: pending.dccQuoteId,
    dccTotalAmount: {
      amount: String(dccAmt.amount),
      currency: dccAmt.currency,
    },
  }
  if (pending.exchangeRate != null && pending.exchangeRate !== '') {
    dccInfo.exchangeRate = pending.exchangeRate
  }
  if (pending.markupRate != null && pending.markupRate !== '') {
    dccInfo.markupRate = pending.markupRate
  }
  body.dccInfo = dccInfo
  return body
}
